import React from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Shared read-only CIF + Postal address presentation (CCF-74 / CCF-77).
 *
 * Renders the customer's **CIF Address** (five labelled lines + Eircode) and
 * the account's **Postal Address** (five labelled lines) side by side, plus the
 * "Change address page" link that routes to the address-change screen. This is
 * the single source of truth for how the agent desktop confirms an address on
 * file — Card Maintenance (CCF-77) and Up-to-date Statements (CCF-74) both use
 * it so the two screens stay identical.
 *
 * Inputs are normalised line arrays; missing lines render blank. Callers that
 * hold the address as a single joined string should split it into lines first.
 */
export interface CustomerAddressPanelProps {
  /** CIF address lines (up to five rendered, in order). */
  cifAddress: string[];
  /**
   * The customer's CIF number (the customer identifier). Rendered beneath the
   * CIF Address heading for the agent to verify (#79). Optional — callers that
   * omit it render the heading without a number, unchanged.
   */
  cifNumber?: string;
  /** Eircode held against the CIF address (rendered blank when absent). */
  eircode?: string;
  /** Postal / delivery address lines (up to five rendered, in order). */
  postalAddress: string[];
  /** Route the "Change address page" link navigates to. */
  changeAddressPath: string;
  /**
   * Optional extra controls rendered in the right-hand action column beneath
   * the "Change address page" link (e.g. Card Maintenance's secure-address
   * field + Update action). Statements passes nothing.
   */
  actions?: React.ReactNode;
}

/** One labelled "Line N" / "Eircode:" address row. */
function AddressLine({
  label,
  value,
}: {
  label: string;
  value: string;
}): React.JSX.Element {
  return (
    <div className="flex items-center">
      <span className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c] w-[80px]">
        {label}
      </span>
      <span className="text-[14px] font-['Arial',sans-serif] text-[#1d252c]">
        {value}
      </span>
    </div>
  );
}

export function CustomerAddressPanel({
  cifAddress,
  cifNumber,
  eircode,
  postalAddress,
  changeAddressPath,
  actions,
}: CustomerAddressPanelProps): React.JSX.Element {
  const navigate = useNavigate();
  return (
    <div className="mb-6 p-4 border-2 border-[#dad7d3] bg-white max-w-[1100px]">
      <div className="grid grid-cols-[1fr_1px_1fr_auto] gap-6 items-start">
        {/* CIF Address */}
        <div>
          <h3
            className={`text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c] ${
              cifNumber ? 'mb-1' : 'mb-4'
            }`}
          >
            CIF Address
          </h3>
          {/* CIF number (the customer identifier) shown for agent verification
              (#79). Rendered only when supplied. */}
          {cifNumber && (
            <p className="text-[14px] font-['Arial',sans-serif] text-[#1d252c] mb-3 m-0">
              <span className="font-bold">CIF:</span> {cifNumber}
            </p>
          )}
          <div className="space-y-2">
            {[0, 1, 2, 3, 4].map((i) => (
              <AddressLine
                key={i}
                label={`Line ${i + 1}`}
                value={cifAddress[i] ?? ''}
              />
            ))}
            <AddressLine label="Eircode:" value={eircode ?? ''} />
          </div>
        </div>

        <div className="h-full w-[1px] bg-[#dad7d3]" aria-hidden="true" />

        {/* Postal Address */}
        <div>
          <h3 className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-4">
            Postal Address
          </h3>
          <div className="space-y-2">
            {[0, 1, 2, 3, 4].map((i) => (
              <AddressLine
                key={i}
                label={`Line ${i + 1}`}
                value={postalAddress[i] ?? ''}
              />
            ))}
          </div>
        </div>

        {/* Change-address route + any caller-supplied actions. */}
        <div className="space-y-4">
          <button
            type="button"
            onClick={() => navigate(changeAddressPath)}
            className="px-6 py-3 bg-[#FF703E] text-[#1d252c] font-['Arial',sans-serif] font-bold text-[14px] rounded-md hover:bg-[#e55a2a] transition-all focus:outline-none focus:ring-4 focus:ring-[#005fcc]/20"
          >
            Change address page
          </button>
          {actions}
        </div>
      </div>
    </div>
  );
}
